import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaProjectDiagram, FaCalendarAlt } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';

import BreadCrumb from '~/components/BreadCrumb';
import ProjectInfo from '~/components/ProjectInfo/Default';
import TextArea from '~/components/TextArea';
import UploadCompletionFiles from '~/components/UploadCompletionFiles';
import UploadFiles from '~/components/UploadFiles';
import SaveButton from '~/components/Buttons/Save';
import ExitButton from '~/components/Buttons/Exit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import NoticeDialog from '~/components/Dialogs/Notice';

import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Content, Wrapper, ButtonContainer } from './styles';

interface IFormProcedure5 {
  type: string;
  code: string;
  validation_type: string;
  project_procedure_id: string;
  record_description: string;
  record_note: string;
  project_temp_id: string;
  project_completion_temp_id: string;
}

interface ILocation {
  id: string;
  type: string;
  code: string;
  status: string;
  event_date: Date;
  project_procedure_id: string;
}

const Procedure5: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [projectTempFilled, setProjectTempFilled] = useState(false);
  const [projectTemp, setProjectTemp] = useState('');
  const [projectCompletionTempFilled, setProjectCompletionTempFilled] =
    useState(false);
  const [projectCompletionTemp, setProjectCompletionTemp] = useState('');
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [titleConfirmProcedure, setTitleConfirmProcedure] = useState('');
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isOpenNotice, setIsOpenNotice] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, setValue, errors } =
    useForm<IFormProcedure5>();

  useEffect(() => {
    if (state === undefined) {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Projeto não localizado',
      });

      history.push('/projects/events');
    } else if (
      new Date().setHours(0, 0, 0, 0) <=
        new Date(state.event_date).setHours(0, 0, 0, 0) &&
      user.user_type.type !== 'ADM'
    ) {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Data de movimentação inválida',
      });

      history.push('/projects/events');
    } else if (state.status.toUpperCase() === 'CANCELADO') {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Projeto Cancelado',
      });

      history.push('/projects/events');
    } else if (state.status.toUpperCase() === 'FINALIZADO') {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Projeto Finalizado',
      });

      history.push('/projects/events');
    } else {
      register('validation_type');
      register('project_temp_id');
      register('project_completion_temp_id');
      register('type');
      register('code');
      register('record_description');
      register('project_procedure_id');

      setValue('type', 'EVENTO');
      setValue('code', state.code);
      setValue('project_procedure_id', state.project_procedure_id);

      if (!projectTempFilled) {
        setProjectTempFilled(true);
        setProjectTemp(uuidv4());
      }

      if (!projectCompletionTempFilled) {
        setProjectCompletionTempFilled(true);
        setProjectCompletionTemp(uuidv4());
      }
    }
  }, [
    addToast,
    history,
    state,
    user,
    projectTempFilled,
    projectCompletionTempFilled,
    register,
    setValue,
  ]);

  useEffect(() => {
    setValue('project_temp_id', projectTemp);
    setValue('project_completion_temp_id', projectCompletionTemp);
  }, [projectTemp, projectCompletionTemp, setValue]);

  const submitForm = useCallback(
    async (data: IFormProcedure5) => {
      try {
        setIsSaving(true);

        await api.put(`/projects/${state.id}`, data);

        addToast({
          type: 'success',
          title: 'Projeto atualizado!',
          description: 'Projeto atualizado com sucesso.',
        });

        history.push('/projects/events');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, state],
  );

  async function onValidateConfirmProcedure(): Promise<void> {
    api
      .get(
        `/projectcompletionfilesreceived/projectcompletiontemp?` +
          `project_completion_temp_id=${projectCompletionTemp}`,
      )
      .then(response => {
        if (response.data.length > 0) {
          setIsOpenConfirmProcedure(true);
          setTitleConfirmProcedure(
            `Confirma finalização da solicitação ${state.code}?`,
          );
        } else {
          setIsOpenNotice(true);
        }
      });
  }

  function onConfirmProcedure(): void {
    setValue('validation_type', 'APROVADO');
    setValue('record_description', `Envio de arquivos de conclusão`);

    handleSubmit(submitForm)();
  }

  const onExit = useCallback(() => {
    api.delete(`projectfilesreceived/projecttempid/${projectTemp}`);

    api.delete(
      `projectcompletionfilesreceived/projectcompletiontempid/${projectCompletionTemp}`,
    );

    history.push('/projects/events');
  }, [history, projectTemp, projectCompletionTemp]);

  function handleFiles(): React.ReactNode {
    if (user.user_type.type !== 'REP') {
      return (
        <Content>
          <UploadFiles
            type="anexo"
            project_temp_id={projectTemp}
            project_type="EVENTO"
            sequence={5}
            user_id={user.id}
          />
        </Content>
      );
    }

    return <></>;
  }

  return (
    <Container>
      <BreadCrumb
        main="Solicitações"
        path="/projects/main"
        firstChildPath="/projects/events"
        firstChild="Eventos"
        secondChild="Atualização Solicitação"
        mainIcon={FaProjectDiagram}
        firstChildIcon={FaCalendarAlt}
        secondChildIcon={FaCalendarAlt}
        mainColored
        firstChildColored
      />

      <ProjectInfo project_id={state.id} />

      <form onSubmit={handleSubmit(submitForm)}>
        <Content>
          <UploadCompletionFiles
            type="conclusao"
            project_completion_temp_id={projectCompletionTemp}
            project_type="EVENTO"
            sequence={5}
            user_id={user.id}
          />
        </Content>

        <Content>
          <Wrapper>
            <TextArea
              name="record_note"
              labelFor="record_note"
              labelText="Observação"
              error={errors?.record_note?.message}
              ref={register}
              maxLength={1000}
            />
          </Wrapper>
        </Content>

        {handleFiles()}

        <ButtonContainer>
          <SaveButton
            label="Finalizar"
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => onValidateConfirmProcedure()}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </ButtonContainer>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={onConfirmProcedure}
        title={titleConfirmProcedure}
        subTitle="O projeto será enviado para o próximo procedimento"
        opConfirm="Sim"
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />

      <NoticeDialog
        isOpen={isOpenNotice}
        type="error"
        title="Necessário anexar arquivos de conclusão!"
        subTitle="Não é possível enviar projeto sem anexar arquivos de conclusão (imagens e/ou vídeos)"
        setIsOpen={setIsOpenNotice}
      />
    </Container>
  );
};

export default Procedure5;
